import { useState } from "react";
import styles from "./NewProductModal.module.css";

function generateNoBarcode() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let rand = "";
  for (let i = 0; i < 5; i++) rand += chars[Math.floor(Math.random() * chars.length)];
  return `NOBC-${Date.now()}-${rand}`;
}

export default function NewProductModal({ barcode: barcodeProp, prefill, gs1Parsed, onSave, onClose }) {
  const isNoBarcode = !barcodeProp;
  const [barcode] = useState(() => barcodeProp || generateNoBarcode());
  const [form, setForm] = useState({
    type: prefill?.type || "Drug",
    description: prefill?.description || "",
    manufacturer: prefill?.manufacturer || "",
    lotNumber: gs1Parsed?.lotNumber || "",
    expiryDate: gs1Parsed?.expiryDate || "",
    minOrderLine: prefill?.minOrderLine ?? "",
  });
  const [lockedFields, setLockedFields] = useState(() => {
    const locked = new Set();
    if (gs1Parsed?.lotNumber) locked.add("lotNumber");
    if (gs1Parsed?.expiryDate) locked.add("expiryDate");
    return locked;
  });
  const [error, setError] = useState("");

  const unlock = (field) =>
    setLockedFields(prev => { const s = new Set(prev); s.delete(field); return s; });

  const handleSave = () => {
    if (!form.description.trim() || !form.manufacturer.trim() || !form.lotNumber.trim()) {
      setError("Description, manufacturer, and lot number are required"); return;
    }
    if (!form.expiryDate) {
      setError("Expiry date is required"); return;
    }
    if (form.minOrderLine !== "" && (isNaN(Number(form.minOrderLine)) || Number(form.minOrderLine) < 0)) {
      setError("Min order line must be a non-negative number"); return;
    }
    const data = {
      id: `${barcode}#${form.lotNumber.trim()}`,
      barcode,
      ...form,
    };
    data.minOrderLine = form.minOrderLine !== "" ? Number(form.minOrderLine) : undefined;
    onSave(data);
  };

  const isPrefilled = !!prefill;
  const hasGs1 = !!gs1Parsed;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.title}>{isPrefilled ? "New Lot" : "New Product"}</div>
          <button onClick={onClose} className={styles.closeBtn}>x</button>
        </div>

        {hasGs1 && (
          <div className={styles.gs1Banner}>
            Fields marked <span className={styles.gs1BadgeInline}>FROM BARCODE</span> were
            read automatically from the barcode scan. Verify before saving.
          </div>
        )}

        <div className={styles.fieldGroup}>
          <div className={styles.label}>Barcode</div>
          <input
            value={isNoBarcode ? "No barcode (auto-generated)" : barcode}
            readOnly
            className={`${styles.input} ${styles.inputReadonly}`}
          />
        </div>

        <div className={styles.fieldGroup}>
          <div className={styles.label}>Type</div>
          <div className={styles.typeButtons}>
            {["Drug", "Supply"].map(t => (
              <button key={t}
                onClick={() => !isPrefilled && setForm(f => ({ ...f, type: t }))}
                className={form.type === t ? styles.typeBtnActive : styles.typeBtn}
                disabled={isPrefilled}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {["description", "manufacturer"].map(field => (
          <div key={field} className={styles.fieldGroup}>
            <div className={styles.label}>{field}</div>
            <input
              value={form[field]}
              onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
              readOnly={isPrefilled}
              placeholder={field === "description" ? "e.g. Vancomycin 1GM" : "e.g. HOSPIRA"}
              className={`${styles.input} ${isPrefilled ? styles.inputReadonly : ""}`}
            />
          </div>
        ))}

        <div className={styles.fieldGroup}>
          <div className={styles.labelRow}>
            <div className={styles.label}>Lot Number</div>
            {lockedFields.has("lotNumber") && (
              <span className={styles.gs1Badge}>
                FROM BARCODE
                <button className={styles.unlockBtn} onClick={() => unlock("lotNumber")}>
                  Edit
                </button>
              </span>
            )}
          </div>
          <input
            value={form.lotNumber}
            onChange={e => setForm(f => ({ ...f, lotNumber: e.target.value }))}
            readOnly={lockedFields.has("lotNumber")}
            placeholder="e.g. LOT-2024-001"
            className={`${styles.input} ${lockedFields.has("lotNumber") ? styles.inputFromBarcode : ""}`}
          />
        </div>

        <div className={styles.fieldGroup}>
          <div className={styles.labelRow}>
            <div className={styles.label}>Expiry Date</div>
            {lockedFields.has("expiryDate") && (
              <span className={styles.gs1Badge}>
                FROM BARCODE
                <button className={styles.unlockBtn} onClick={() => unlock("expiryDate")}>
                  Edit
                </button>
              </span>
            )}
          </div>
          <input
            type="date"
            value={form.expiryDate}
            onChange={e => setForm(f => ({ ...f, expiryDate: e.target.value }))}
            readOnly={lockedFields.has("expiryDate")}
            className={`${styles.input} ${lockedFields.has("expiryDate") ? styles.inputFromBarcode : ""}`}
          />
        </div>

        <div className={styles.fieldGroup}>
          <div className={styles.label}>Min Order Line <span className={styles.hint}>(Optional)</span></div>
          <input
            type="number"
            min="0"
            value={form.minOrderLine}
            onChange={e => setForm(f => ({ ...f, minOrderLine: e.target.value }))}
            placeholder="Reorder threshold, e.g. 50"
            className={styles.input}
          />
        </div>

        {error && <div className={styles.error}>{error}</div>}
        <button onClick={handleSave} className={styles.submitBtn}>
          {isPrefilled ? "SAVE NEW LOT" : "SAVE PRODUCT"}
        </button>
      </div>
    </div>
  );
}
